import React, { useState } from 'react';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Divider } from 'ui-components/components/divider';
import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { SimpleDropdown } from '../../../../../../components/simple-dropdown';
import { useModal } from '../../../../../../hooks/use-modal';
import { purchaseOrderProperties } from '../../../data/purchase-order-properties';
import { usePurchaseOrderContext } from '../../../purchase-order-context';
import { messages } from '../purchase-order-details-messages';
import styles from '../purchase-order-details.styles.module.less';
import { SupplierModal } from '../supplier-modal/supplier-modal';

/**
 * Editable Supplier Section component
 * Displays supplier and warehouse selection dropdowns with add/edit capabilities
 * Only rendered in edit mode (draft status)
 * @param {object} props - Component props
 * @param {object} props.currentSupplier - The current supplier data object
 * @param {(field: string, value: number | string) => void} props.onInputChange - Callback when supplier or warehouse changes
 * @param {() => void} props.refetchSupplier - Function to refetch current supplier data
 * @returns {JSX.Element} The editable supplier section
 */
export const EditableSupplierSection = ({ currentSupplier, onInputChange, refetchSupplier }) => {
  const { purchaseOrder, warehouses, getFieldError, suppliers, refetchSuppliers } = usePurchaseOrderContext();

  const { isOpen: isSupplierModalOpen, open: openSupplierModal, close: closeSupplierModal } = useModal(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const addSupplier = () => {
    setSelectedSupplier(null);
    setIsEditMode(false);
    openSupplierModal();
  };

  const editSupplier = () => {
    if (!purchaseOrder.supplierId || !currentSupplier) {
      return;
    }

    setIsEditMode(true);
    setSelectedSupplier(currentSupplier);
    openSupplierModal();
  };

  const handleSupplierModalClose = () => {
    closeSupplierModal();
    setSelectedSupplier(null);
    setIsEditMode(false);
  };

  const handleSupplierSaved = () => {
    refetchSuppliers();
    if (isEditMode && purchaseOrder.supplierId) {
      refetchSupplier();
    }
  };

  return (
    <>
      <div className={classnames(styles['edit-row-container'], styles['supplier-container'])}>
        <Text className={styles['edit-row-header']} textType={TextType.H4} value={messages.supplierInformation} />
        <FlexRow className={styles['edit-row']}>
          <FlexColumn className={styles.column}>
            <SearchableDropdownField
              className={styles['supplier-dropdown']}
              isRequiredError={getFieldError(purchaseOrderProperties.supplierId)}
              label={<FormattedMessage {...messages.selectSupplier} />}
              onItemSelected={(value) => {
                onInputChange(purchaseOrderProperties.supplierId, value);
              }}
              options={suppliers}
              required
              selectedItemValue={purchaseOrder.supplierId}
            />
            <div className={styles['supplier-buttons']}>
              <span className={styles['add-edit-supplier-button']}>
                <Button onClick={addSupplier} theme={THEME_CONSTANTS.link}>
                  <FontAwesomeIcon className={styles['add-edit-supplier-icon']} icon={faPlus} />
                  <FormattedMessage {...messages.addSupplier} />
                </Button>
              </span>
              {purchaseOrder.supplierId ? (
                <span className={styles['add-edit-supplier-button']}>
                  <Button onClick={editSupplier} theme={THEME_CONSTANTS.link}>
                    <FontAwesomeIcon className={styles['add-edit-supplier-icon']} icon={faPenToSquare} />
                    <FormattedMessage {...messages.editSupplier} />
                  </Button>
                </span>
              ) : null}
            </div>
          </FlexColumn>
          <FlexColumn className={styles.column}>
            <Text className={styles['label-text-for-warehouse']} textType={TextType.Label} value={messages.selectWarehouse} />
            <SimpleDropdown
              defaultTitle={<FormattedMessage {...messages.selectWarehouse} />}
              dropdownMenuShouldHaveWidthOfButton
              hasError={!!getFieldError(purchaseOrderProperties.warehouseId)}
              items={warehouses}
              onItemSelected={(value) => onInputChange(purchaseOrderProperties.warehouseId, value)}
              required
              selectedItemKey={purchaseOrder.warehouseId}
            />
          </FlexColumn>
        </FlexRow>
        <Divider className={styles.divider} orientation='horizontal' />
      </div>
      {isSupplierModalOpen ? (
        <SupplierModal
          isEditing={isEditMode}
          onClose={handleSupplierModalClose}
          onSupplierSaved={handleSupplierSaved}
          supplier={selectedSupplier}
        />
      ) : null}
    </>
  );
};
